import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CreateApiKeyRequest, UpdateApiKeyRequest } from '@nucleus-commerce/api-client/admin';

import { apiKeysApi } from '../api/api-keys-api';

import { apiKeyKeys } from './api-key-keys';

/** The response carries the full key once; the dialog shows it and nothing caches it. */
export function useCreateApiKey(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateApiKeyRequest) => apiKeysApi.create(storeId, body),
    meta: { successMessage: 'apiKeys:toasts.created', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: apiKeyKeys.all(storeId) }),
  });
}

/** Origins, scopes and the rate limit take effect within a minute; no new key to deploy. */
export function useUpdateApiKey(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateApiKeyRequest }) =>
      apiKeysApi.update(storeId, id, body),
    meta: { successMessage: 'apiKeys:toasts.updated', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: apiKeyKeys.all(storeId) }),
  });
}

export function useRevokeApiKey(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(storeId, id),
    meta: { successMessage: 'apiKeys:toasts.revoked', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: apiKeyKeys.all(storeId) }),
  });
}

export function useDeleteApiKey(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiKeysApi.remove(storeId, id),
    meta: { successMessage: 'apiKeys:toasts.deleted', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: apiKeyKeys.all(storeId) }),
  });
}
